import { RightInvocationDTO } from '@ketch-com/figurehead'
import { useCreateRightInvocationNote } from 'api/rightInvocationsV2/mutations/useCreateRightInvocationNote'
import { useDeleteRightInvocationNote } from 'api/rightInvocationsV2/mutations/useDeleteRightInvocationNote'
import { useQueryClient } from 'react-query'
import { showToast } from 'components/modals/AlertComponent'
import { FileInfoDTO } from 'interfaces/files/fileInfo'
import { ApiQueryKeys } from 'api/common/queryKeys'

export const useAttachmentManagement = (rightInvocation: RightInvocationDTO | null) => {
  const queryClient = useQueryClient()

  const createNote = useCreateRightInvocationNote({
    onSuccess: () => {
      queryClient.invalidateQueries([ApiQueryKeys.rightInvocationV2])
      showToast({ content: 'Changes saved successfully', type: 'success' })
    },
    onError: () => {
      showToast({ content: 'Unable to save changes', type: 'error' })
    },
  })

  const deleteNote = useDeleteRightInvocationNote({
    onSuccess: () => {
      queryClient.invalidateQueries([ApiQueryKeys.rightInvocationV2])
      showToast({ content: 'Successfully deleted', type: 'success' })
    },
    onError: () => {
      showToast({ content: 'Unable to delete', type: 'error' })
    },
  })

  const formatAttachments = (rightInvocation: RightInvocationDTO | null): FileInfoDTO[] => {
    if (!rightInvocation || !Array.isArray(rightInvocation.rightInvocationNotes)) return []

    return rightInvocation.rightInvocationNotes.flatMap(note => {
      if (!note.attachments || !Array.isArray(note.attachments)) return []

      return note.attachments.map(att => {
        const name = att.attachmentName ?? 'Unknown'
        const encodedName = encodeURIComponent(name).replace(/%20/g, '+')

        return {
          public_url: att.attachmentURL ?? '',
          ID: note.id ?? '',
          content_type: att.contentType ?? '',
          download_url: `/api/v2/files?bucket=&folder=${
            note.rightInvocationID ?? ''
          }&key=${encodedName}&version=resolution_attachments`,
          info_url: `/api/v2/files/info?bucket=&folder=${
            note.rightInvocationID ?? ''
          }&key=${encodedName}&version=resolution_attachments`,
          name,
          size: att.size ?? '',
          extension: name.split('.').pop() ?? '',
          createdAt: note.createdAt,
          createdBy: note.createdBy,
        }
      })
    })
  }

  const handleUploadAttachment = (uploadedFiles: FileInfoDTO[]) => {
    if (!rightInvocation?.id) return

    const attachmentPayloads = uploadedFiles.map(file => ({
      attachmentName: file.name,
      attachmentUrl: file.public_url,
      contentType: file.content_type,
      size: file.size,
    }))

    createNote.mutate({
      params: {
        rightInvocationID: rightInvocation.id,
        formData: { attachments: attachmentPayloads },
      },
    })
  }

  const handleDeleteAttachment = (file: FileInfoDTO) => {
    if (!rightInvocation?.id) return
    deleteNote.mutate({
      params: {
        rightInvocationID: rightInvocation.id,
        noteID: file.ID,
      },
    })
  }

  return {
    formatAttachments,
    handleUploadAttachment,
    handleDeleteAttachment,
  }
}
